import { Injectable, inject } from '@angular/core';
import {
  DocumentManagementApi,
  type DocumentDetailResponse,
  type DocumentExtractionListResponse,
  type DocumentExtractionResponse,
  type DocumentListResponse,
  type DocumentResponse,
  type DocumentVersionResponse,
  type InitiateDocumentUploadResponse,
  type PatchDocument,
  type PresignedDownloadResponse,
  type StartAnalysisResponse,
} from '@tourfold/libraries/tf-api-client-public-v2';

/**
 * Thin wrapper over the generated `DocumentManagementApi`. Stays promise-first to match
 * `FoldersService` and the rest of the platform/object-views services.
 *
 * Upload is a three-step dance — the wrapper exposes the individual steps so the caller can
 * orchestrate progress UI:
 *  1. {@link initiateUpload}: server creates draft document + version, returns a presigned form
 *  2. caller POSTs the binary to `upload.url` with `formFields + file` (see `uploadBinary` below)
 *  3. {@link finalizeUpload}: server attaches the uploaded blob to the version
 */
@Injectable({ providedIn: 'root' })
export class DocumentsService {
  private readonly api = inject(DocumentManagementApi);

  listInFolder(
    folderId: string,
    options: { page?: number; size?: number; sort?: string[] } = {},
  ): Promise<DocumentListResponse> {
    return this.api.listDocumentsInFolder({
      folderId,
      page: options.page,
      size: options.size,
      sort: options.sort,
    });
  }

  getById(id: string): Promise<DocumentDetailResponse> {
    return this.api.getDocumentById({ id });
  }

  initiateUpload(args: {
    name: string;
    folderId: string;
    mimeType: string;
  }): Promise<InitiateDocumentUploadResponse> {
    return this.api.initiateDocumentUpload({
      initiateDocumentUploadRequest: {
        name: args.name,
        folderId: args.folderId,
        mimeType: args.mimeType,
      },
    });
  }

  finalizeUpload(documentId: string): Promise<DocumentResponse> {
    return this.api.finalizeDocumentUpload({ id: documentId });
  }

  /**
   * Move a document into another folder. Convenience wrapper over [patch] that targets only the
   * `folder_id` field — keeps the drag-and-drop call site short.
   */
  move(documentId: string, targetFolderId: string): Promise<DocumentResponse> {
    return this.patch(documentId, { folderId: targetFolderId });
  }

  /**
   * Returns a short-lived presigned download URL for the given version.
   *
   * `rendition` selects the filestore variant to serve:
   *   - `'original'` (default) — the bytes the user uploaded.
   *   - `'preview'` — the inline JPEG preview, only present when `preview_available` is `true`
   *     on the version (PDFs and supported image MIMEs).
   */
  download(
    documentId: string,
    versionId: string,
    rendition?: 'original' | 'preview',
  ): Promise<PresignedDownloadResponse> {
    return this.api.downloadDocumentVersion({ id: documentId, versionId, rendition });
  }

  listVersions(documentId: string): Promise<DocumentVersionResponse[]> {
    return this.api.listDocumentVersions({ id: documentId });
  }

  /**
   * Recent uploads visible to the caller. With `folderId`, scoped to that folder's subtree
   * (folder + descendants). Without `folderId`, tenant-wide. Backend already excludes soft-deleted
   * rows and orders by current-version `uploaded_at` desc.
   */
  listRecent(args: { folderId?: string; limit?: number; since?: string } = {}): Promise<DocumentResponse[]> {
    return this.api.listRecentDocuments({
      folderId: args.folderId,
      limit: args.limit,
      since: args.since,
    });
  }

  initiateVersionUpload(args: {
    documentId: string;
    mimeType: string;
    originalFileName?: string;
    changeNote?: string;
  }): Promise<InitiateDocumentUploadResponse> {
    return this.api.initiateDocumentVersionUpload({
      id: args.documentId,
      initiateVersionUploadRequest: {
        mimeType: args.mimeType,
        originalFileName: args.originalFileName,
        changeNote: args.changeNote,
      },
    });
  }

  /**
   * Soft-deletes the document — moves it to the user's Trash. The row remains restorable until
   * a subsequent hard-delete (`delete()`) permanently removes it.
   */
  softDelete(documentId: string): Promise<DocumentResponse> {
    return this.api.softDeleteDocument({ id: documentId });
  }

  /**
   * Restores a soft-deleted document. The optional [options] body picks the destination and how to handle
   * a name collision in the target folder:
   *  - `targetFolderId`: explicit destination. Omit to walk the captured `original_path` (the
   *    backend auto-creates any missing folders along the way).
   *  - `conflictResolution = "rename"` + `newName`: restore under a new name in the target folder.
   *  - `conflictResolution = "new_version"`: append the restored doc as a new version of the
   *    existing live sibling (MIME types must match).
   *  - omit `conflictResolution`: a name collision raises `document-management/name-taken` (409).
   */
  restore(
    documentId: string,
    options: {
      targetFolderId?: string;
      conflictResolution?: 'rename' | 'new_version';
      newName?: string;
    } = {},
  ): Promise<DocumentResponse> {
    return this.api.restoreDocument({
      id: documentId,
      restoreDocumentRequest: {
        targetFolderId: options.targetFolderId,
        conflictResolution: options.conflictResolution,
        newName: options.newName,
      },
    });
  }

  delete(documentId: string): Promise<void> {
    return this.api.deleteDocument({ id: documentId });
  }

  /**
   * Removes a document by archiving it and then hard-deleting it. The two-step dance is required
   * because the backend only allows hard-delete on soft-deleted documents — see
   * `document-management/must-be-soft-deleted-first`. Used by the UI to clean up incomplete
   * uploads (rows whose `current_version_id` is still null because finalize failed).
   */
  async hardRemove(documentId: string): Promise<void> {
    await this.softDelete(documentId);
    await this.delete(documentId);
  }

  /**
   * JSON Merge Patch (RFC 7386). Field omitted = unchanged; `description: null` clears it.
   * The OpenAPI generator types `description` as `string | undefined`, but the API treats `null`
   * as "clear" — we cast through here so callers can pass `null` naturally.
   *
   * `folderId` moves the document into another folder atomically. Backend rejects targets that
   * are `managed_branch` with `invalid_move_target` (HTTP 422) and surfaces collisions
   * with `name_taken` (HTTP 409).
   */
  patch(
    documentId: string,
    patch: { name?: string; description?: string | null; folderId?: string; tagIds?: Set<string> },
  ): Promise<DocumentResponse> {
    // Use the generated `PatchDocument` DTO so the serializer in the API client picks up the
    // right snake_case JSON keys. The generator types `description` as `string | undefined`
    // because typescript-fetch doesn't translate OpenAPI `nullable: true` into `string | null`,
    // but the runtime serializer happily forwards `null` if we put it there — narrow with a
    // single-field cast at the assignment site rather than widening the whole body type.
    const body: PatchDocument = {};
    if (patch.name !== undefined) body.name = patch.name;
    if (patch.description !== undefined) {
      (body as { description?: string | null }).description = patch.description;
    }
    if (patch.folderId !== undefined) body.folderId = patch.folderId;
    if (patch.tagIds !== undefined) body.tagIds = patch.tagIds;
    return this.api.updateDocument({ id: documentId, patchDocument: body });
  }

  // ── AI extraction ────────────────────────────────────────────────────
  // The detail pane observes extraction lifecycle via documents SSE and uses these endpoints for
  // initial state plus fallback refreshes while an extraction is still running.

  analyze(documentId: string, versionId: string): Promise<StartAnalysisResponse> {
    return this.api.analyzeDocumentVersion({ id: documentId, versionId });
  }

  listExtractions(documentId: string, versionId: string): Promise<DocumentExtractionListResponse> {
    return this.api.listDocumentVersionExtractions({ id: documentId, versionId });
  }

  /**
   * Most recent `succeeded` extraction for the version, or `null` when nothing has succeeded yet
   * (the API returns 404 in that case).
   */
  async getLatestSucceededExtraction(
    documentId: string,
    versionId: string,
  ): Promise<DocumentExtractionResponse | null> {
    try {
      return await this.api.getLatestSucceededExtraction({ id: documentId, versionId });
    } catch (err: unknown) {
      if (isNotFound(err)) return null;
      throw err;
    }
  }

  getExtractionById(extractionId: string): Promise<DocumentExtractionResponse> {
    return this.api.getExtractionById({ extractionId });
  }
}

function isNotFound(err: unknown): boolean {
  return typeof err === 'object' && err !== null && 'response' in err
    && (err as { response?: Response }).response?.status === 404;
}

/**
 * Upload progress for a presigned form POST. `percent` is `null` if the browser cannot compute
 * the full multipart body size.
 */
export interface BinaryUploadProgress {
  readonly loaded: number;
  readonly total: number | null;
  readonly percent: number | null;
}

/**
 * POSTs `file` to a presigned form URL with the server-provided `formFields`. Resolves on 2xx,
 * rejects with the response status text on any other code. Caller is responsible for translating
 * the rejection into a user-facing error.
 */
export function uploadBinary(
  url: string,
  formFields: Record<string, string>,
  file: File,
  onProgress?: (progress: BinaryUploadProgress) => void,
): Promise<void> {
  const formData = new FormData();
  for (const [key, value] of Object.entries(formFields)) formData.append(key, value);
  formData.append('file', file);

  return new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.onprogress = (event) => {
      const total = event.lengthComputable && event.total > 0 ? event.total : null;
      onProgress?.({
        loaded: event.loaded,
        total,
        percent: total === null ? null : Math.min(100, Math.round((event.loaded / total) * 100)),
      });
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress?.({ loaded: file.size, total: file.size, percent: 100 });
        resolve();
        return;
      }
      reject(new Error(`Storage upload failed (${xhr.status})`));
    };
    xhr.onerror = () => reject(new Error('Storage upload failed (network error)'));
    xhr.onabort = () => reject(new Error('Storage upload aborted'));

    xhr.open('POST', url);
    xhr.send(formData);
  });
}
